import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "flex gap-x4 items-center mt-x4 md:mt-x5" }
const _hoisted_2 = ["disabled"]

import ProductDetailPurchaseOptions from '@/shared/product/purchase/ui/ProductPurchaseOptions.vue';
import AddToBasketButtonContent from '@/shared/form/AddToBasketButtonContent.vue';
import { Form } from 'vee-validate';
import { computed, nextTick, Ref, ref, watch } from 'vue';
import utilities from '@/shared/utilities.service';
import { PurchaseOptionType } from '@/types/productServerApi';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import useProductClickAndCollect from '@/shared/product/purchase/ui/useProductClickAndCollect';
import useProductBasketButtonState from '@/shared/product/purchase/ui/useProductBasketButtonState';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import ProductPurchaseClickAndCollectStoreInfo
    from '@/shared/product/purchase/ui/ProductPurchaseClickAndCollectStoreInfo.vue';
import ProductPurchaseClickAndCollectStoreSelector
    from '@/shared/product/purchase/ui/ProductPurchaseClickAndCollectStoreSelector.vue';
import useProductDetailUpsell from '@/shared/product/upsell/useProductDetailUpsell';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import { hardRedirect } from '@/core/navigation/redirect.service';
import siteContextStore from '@/shared/content/site-context.store';
import WishlistButton from '@/shared/product/wishlist/ui/WishlistButton.vue';
import useProductAddOns from '@/shared/product/add-ons/useProductAddOns';
import useTEMPB2CFeatureBasket from '@/b2c/basket/useTEMPB2CFeatureBasket';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';
import { useAddToBasket } from '@/b2c/product/purchase/useAddToBasket';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDetailPurchase',
  setup(__props) {

const editGuid = useRouteQuery(QueryKeys.Edit);
const labels = useBlockLabels();

const { basketButtonIsDisabled } = useProductBasketButtonState();
const { productData, variantId, productName, moduleConfiguratorTileData } = useProductDetailData();
const {
    addToBasketOnStoreSelection,
    showStoreInfo,
    showStoreSelector,
    storeId,
    openStoreSelector,
    setAddToBasketOnStoreSelection,
    setStoreId,
    setStoreInventoryData,
} = useProductClickAndCollect();
const { maybeInitUpsellItems } = useProductDetailUpsell();
const { forceAddingState, addAddOnsToBasketSynchronous, addedAddOnsLabel, hasSelectedAddOns } = useProductAddOns();

const { addClickAndCollect, replaceItem, changeClickAndCollectStore, hasClickAndCollect, clickAndCollectStoreId } = useTEMPB2CFeatureBasket();
const { setBasketDisabledState } = useProductBasketButtonState();
const { addToBasket } = useAddToBasket();

const pending: Ref<boolean> = ref(false);
const timing: number = 2000;

const noAvailablePurchaseOptions = computed(() => productData.value.purchasePossibilities.length < 1);
const basketButtonAddedText = computed(() => {
    if (editGuid.value) return labels.addToBasketUpdated;
    if (addedAddOnsLabel.value) return addedAddOnsLabel.value;
    return labels.addedToBasket;
});
const basketButtonDefaultText = computed(() => {
    return basketButtonIsDisabled.value || noAvailablePurchaseOptions.value
        ? labels.notInStock
        : editGuid.value
            ? labels.addToBasketUpdate
            : labels.addToBasket;
});

const adoptStoreIdFromBasket = (id) => {
    if (id) {
        nextTick(() => setStoreId(id));
    }
};

watch(clickAndCollectStoreId, adoptStoreIdFromBasket, { immediate: true });

const submit = async(form: { purchaseOptionType: PurchaseOptionType }): Promise<void> => {
    basketTrackingService.product(productData.value, { purchaseOption: form.purchaseOptionType, quantity: 1 });

    if (form.purchaseOptionType === PurchaseOptionType.ClickAndCollect && storeId.value === null) {
        openStoreSelector();
        setAddToBasketOnStoreSelection(true);
        return;
    }

    if (!pending.value && form.purchaseOptionType !== undefined) {
        pending.value = true;
        const animationTiming = utilities.delay(timing);
        try {
            forceAddingState();

            await addOrEdit(form.purchaseOptionType);

            await handleAddOns(form.purchaseOptionType);

            if (form.purchaseOptionType === PurchaseOptionType.ClickAndCollect) {
                await setStoreInventoryData(variantId.value);
            }
            if (!editGuid.value) {
                await maybeInitUpsellItems(form.purchaseOptionType);
            }
            await animationTiming;
        } finally {
            pending.value = false;
        }
    }
};

const handleAddOns = async(purchaseOptionType: PurchaseOptionType) => {
    if (hasSelectedAddOns.value) {
        await addAddOnsToBasketSynchronous(addToBasket, purchaseOptionType);
    }
};

const addOrEdit = async(purchaseType: PurchaseOptionType) => {
    if (editGuid.value && purchaseType !== PurchaseOptionType.ModuleConfiguration) {
        await replaceItem(editGuid.value, variantId.value);
        hardRedirect(siteContextStore.settings.basketPageUrl);
    } else if (editGuid.value && purchaseType === PurchaseOptionType.ModuleConfiguration) {
        await addToBasket(variantId.value, purchaseType);
        hardRedirect(siteContextStore.settings.basketPageUrl);
    } else {
        await addToBasket(variantId.value, purchaseType);
    }
};

const onStoreSelection = async(): Promise<void> => {
    if (!pending.value && addToBasketOnStoreSelection.value) {
        await handleAddToBasketOnStoreSelection();
    } else {
        await changeClickAndCollectStore(storeId.value!, variantId.value);
        setBasketDisabledState(false);
    }
    await setStoreInventoryData(variantId.value);
};

const handleAddToBasketOnStoreSelection = async() => {
    pending.value = true;
    const animationTiming = utilities.delay(timing);
    try {
        await addClickAndCollect(variantId.value, 1, storeId.value as string);
        await animationTiming;
    } finally {
        pending.value = false;
        setAddToBasketOnStoreSelection(false);
    }
};

const showAddToBasketButton = computed(() => {
    const showRequestDelivery = productData.value.purchasePossibilities.length === 1 ? productData.value.purchasePossibilities[0].type === PurchaseOptionType.Special : false;
    const showInStoreOnly = productData.value.purchasePossibilities.length === 1 ? productData.value.purchasePossibilities[0].type === PurchaseOptionType.InStoreOnly : false;
    return !showRequestDelivery && !showInStoreOnly;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Form), { onSubmit: submit }, {
      default: _withCtx(() => [
        _withDirectives(_createVNode(ProductDetailPurchaseOptions, null, null, 512), [
          [_vShow, !_unref(editGuid)]
        ]),
        _createElementVNode("div", _hoisted_1, [
          (_unref(showAddToBasketButton))
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "submit",
                disabled: _unref(basketButtonIsDisabled) || _unref(noAvailablePurchaseOptions),
                class: "c-button c-button--primary min-w-220 transition-colors"
              }, [
                _createVNode(AddToBasketButtonContent, {
                  pending: pending.value,
                  "added-button-text": _unref(basketButtonAddedText),
                  "default-button-text": _unref(basketButtonDefaultText)
                }, null, 8, ["pending", "added-button-text", "default-button-text"])
              ], 8, _hoisted_2))
            : _createCommentVNode("", true),
          (!(_unref(moduleConfiguratorTileData) && _unref(moduleConfiguratorTileData).modules))
            ? (_openBlock(), _createBlock(WishlistButton, {
                key: 1,
                sku: _unref(variantId),
                name: _unref(productName)
              }, null, 8, ["sku", "name"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    (_unref(showStoreSelector))
      ? (_openBlock(), _createBlock(ProductPurchaseClickAndCollectStoreSelector, {
          key: 0,
          "basket-has-click-and-collect": _unref(hasClickAndCollect),
          onOnStoreSelection: onStoreSelection
        }, null, 8, ["basket-has-click-and-collect"]))
      : _createCommentVNode("", true),
    (_unref(showStoreInfo))
      ? (_openBlock(), _createBlock(ProductPurchaseClickAndCollectStoreInfo, { key: 1 }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})